import React from "react";
import { Box, SxProps, Theme, Typography, useTheme } from "@mui/material";
import { ThinkingOrb, OrbState } from "thinking-orbs";

/**
 * Display sizes in CSS px. The orb library only ships tuned presets at
 * 20/32/64 px, so larger variants render the 64 px preset and upscale the
 * canvas via CSS (dots are soft round marks, so the scale reads cleanly).
 */
const SIZE_PX = {
  inline: 20,
  small: 32,
  medium: 72,
  large: 128,
  xlarge: 168,
} as const;

export type OrbLoaderSize = keyof typeof SIZE_PX;

interface OrbLoaderProps {
  size?: OrbLoaderSize;
  state?: OrbState;
  /** Optional caption shown under the orb (ignored for inline/small). */
  label?: string;
  /** Wrap in a centered flex container with vertical padding. */
  centered?: boolean;
  /** Force light ink, e.g. on a contained/primary button. */
  onDark?: boolean;
  sx?: SxProps<Theme>;
}

export const OrbLoader: React.FC<OrbLoaderProps> = ({
  size = "large",
  state = "working",
  label,
  centered = false,
  onDark = false,
  sx,
}) => {
  const theme = useTheme();
  const px = SIZE_PX[size];
  const preset = px <= 20 ? 20 : px <= 32 ? 32 : 64;
  const orbTheme = onDark || theme.palette.mode === "dark" ? "dark" : "light";
  const showLabel = Boolean(label) && px > 32;

  const orb = (
    <ThinkingOrb
      state={state}
      size={preset}
      theme={orbTheme}
      aria-hidden
      style={{ width: px, height: px }}
    />
  );

  return (
    <Box
      role="progressbar"
      aria-label={label ?? "Loading"}
      aria-busy
      sx={[
        {
          display: centered || showLabel ? "flex" : "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          ...(centered && { width: "100%", py: 4 }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {orb}
      {showLabel && (
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      )}
    </Box>
  );
};

export default OrbLoader;
